import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mail, Calendar, Search, ArrowLeft, Loader2 } from 'lucide-react';

import { emailLogService } from '@/services/email-log.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import { EMAIL_LOGS_PERMISSION } from '@/constants/constant-permissions';
import {
  EmailLogTabs,
  EmailLogList,
  EmailLogDetail,
  TabType,
} from '@/components/email-log';
import { EmailLog, EmailLogFilterDto, EmailLogStatus, EmailLogType } from '@/types/email-log.types';
import { useDebounce } from '@/utils/debounce';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { formatDate } from '@/utils/dateFormat';

const ITEMS_PER_PAGE = 20;

export default function EmailLogPage() {
  // Permission guard
  usePermissionRouteAccess([EMAIL_LOGS_PERMISSION.ADMIN_EMAIL_LOGS_READ]);

  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [emailType, setEmailType] = useState<string>('');
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  const [showDetail, setShowDetail] = useState(false);
  const [isLoadingArchivedDetail, setIsLoadingArchivedDetail] = useState(false);

  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);

  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);

  // Debounced search value
  const debouncedSearch = useDebounce(searchTerm);

  // Build filters
  const filters: EmailLogFilterDto = useMemo(() => {
    const f: EmailLogFilterDto = {
      page: currentPage,
      limit: itemsPerPage,
      sort_by: 'created_at',
      sort_order: 'desc',
    };

    // Send the full local day as ISO timestamps; a bare yyyy-mm-dd is read as UTC midnight,
    // which made a same-day range empty and dropped the last day of any range
    if (dateFrom) {
      f.date_from = new Date(`${dateFrom}T00:00:00`).toISOString();
    }
    if (dateTo) {
      f.date_to = new Date(`${dateTo}T23:59:59.999`).toISOString();
    }

    if (debouncedSearch) {
      f.search = debouncedSearch;
    }

    if (emailType) {
      f.email_type = emailType as EmailLogType;
    }

    if (includeArchived) {
      f.include_archived = true;
    }

    if (activeTab !== 'all') {
      if (activeTab === 'sent') {
        f.status = 'sent' as EmailLogStatus;
      } else if (activeTab === 'pending') {
        f.status = 'pending' as EmailLogStatus;
      } else {
        f.status = activeTab as EmailLogStatus;
      }
    }

    return f;
  }, [currentPage, itemsPerPage, debouncedSearch, activeTab, dateFrom, dateTo, emailType, includeArchived]);

  // Fetch email type options for filter dropdown
  const { data: emailTypeOptions = [] } = useQuery({
    queryKey: ['emailTypeOptions'],
    queryFn: () => emailLogService.getEmailTypeOptions(),
    staleTime: 300000, // Cache for 5 minutes
  });

  // Fetch email logs
  const {
    data: logsData,
    isLoading: isLoadingLogs,
    isFetching,
    error: logsError,
  } = useQuery({
    queryKey: ['emailLogs', filters],
    queryFn: () => emailLogService.getEmailLogs(filters),
    placeholderData: keepPreviousData,
    refetchInterval: 30000, // Auto-refresh every 30s
  });

  // Sync selected log with latest data (updates status on refetch)
  useEffect(() => {
    if (!logsData) return;
    const logs = logsData.logs || [];
    if (selectedLog) {
      // Archived rows are fetched in full separately (S3) — don't clobber with the light list row.
      if (selectedLog.from_archive) return;
      const updatedLog = logs.find((log) => log._id === selectedLog._id);
      if (updatedLog) {
        setSelectedLog(updatedLog);
      } else {
        setSelectedLog(null);
        setShowDetail(false);
      }
    }
  }, [logsData]);

  // Fetch metrics for tab counts. Include archived so the tab totals match the
  // (hot + archived) list total when the "Include archived" toggle is on.
  const { data: metricsData } = useQuery({
    queryKey: ['emailLogMetrics', includeArchived],
    queryFn: () => emailLogService.getMetrics(undefined, undefined, includeArchived),
    staleTime: 30000,
    refetchInterval: 30000, // Auto-refresh to keep tab counts in sync
  });

  // Retry mutation
  const retryMutation = useMutation({
    mutationFn: (logId: string) => emailLogService.retryEmail(logId),
    onSuccess: (updatedLog) => {
      toast.success('Email scheduled for retry');
      queryClient.invalidateQueries({ queryKey: ['emailLogs'] });
      queryClient.invalidateQueries({ queryKey: ['emailLogMetrics'] });
      setSelectedLog(updatedLog);
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to retry email');
    },
  });

  // Handle tab change
  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedLog(null);
    setShowDetail(false);
  }, []);

  // Handle log selection. Archived rows carry only light fields (no html/text body),
  // so fetch the full record from cold storage (S3) by id before showing detail.
  const handleSelectLog = useCallback((log: EmailLog) => {
    setSelectedLog(log);
    setShowDetail(true);
    if (log.from_archive) {
      setIsLoadingArchivedDetail(true);
      emailLogService
        .getEmailLogById(log._id)
        .then((full) => setSelectedLog({ ...full, from_archive: true }))
        .catch(() => { /* keep the light row; body just won't show */ })
        .finally(() => setIsLoadingArchivedDetail(false));
    }
  }, []);

  // Handle back from detail (mobile)
  const handleBackToList = useCallback(() => {
    setShowDetail(false);
  }, []);

  // Handle retry
  const handleRetry = useCallback((log: EmailLog) => {
    retryMutation.mutate(log._id);
  }, [retryMutation]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  // Handle date change
  const handleDateFromChange = useCallback((value: string) => {
    setDateFrom(value);
    setCurrentPage(1);
  }, []);

  const handleDateToChange = useCallback((value: string) => {
    setDateTo(value);
    setCurrentPage(1);
  }, []);

  const handleClearDates = useCallback(() => {
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  }, []);

  // Handle email type change
  const handleEmailTypeChange = useCallback((value: string | number | null) => {
    setEmailType(value as string || '');
    setCurrentPage(1);
  }, []);

  // Metrics for tabs
  const tabCounts = useMemo(() => {
    if (!metricsData) return {};
    return {
      // `all` = the true total of EVERY status (matches the list pagination total),
      all: metricsData.total || 0,
      sent: metricsData.total_sent || 0,
      delivered: metricsData.total_delivered || 0,
      failed: metricsData.total_failed || 0,
      pending: metricsData.total_pending || 0,
      retry_scheduled: metricsData.total_retry_scheduled || 0,
      skipped: metricsData.total_skipped || 0,
      bounced: metricsData.total_bounced || 0,
    };
  }, [metricsData]);

  const logs = logsData?.logs || [];
  const totalPages = logsData?.total_pages || 1;
  const totalItems = logsData?.total || 0;

  return (
    <div className="flex flex-col bg-gray-50 overflow-hidden gap-2" style={{ height: 'calc(100vh - 105px)' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-cyan-600 to-brand-teal-600 rounded-xl px-4 sm:px-6 py-3 sm:py-4 text-white flex-shrink-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold">Email Logs</h1>
              <p className="text-brand-cyan-100 text-xs sm:text-sm mt-0.5 sm:mt-1">Monitor and track email communications</p>
            </div>
          </div>
          {/* Date Range Filter */}
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {/* From Date */}
            <div
              className="relative flex-1 sm:flex-none sm:w-40 cursor-pointer"
              onClick={() => dateFromRef.current?.showPicker()}
            >
              <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-xs sm:text-sm border border-white/30 rounded-lg bg-white/20 text-white">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/70 flex-shrink-0" />
                <span className={dateFrom ? 'text-white' : 'text-white/70'}>
                  {dateFrom ? formatDate(dateFrom, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                </span>
              </div>
              <input
                ref={dateFromRef}
                type="date"
                value={dateFrom}
                onChange={(e) => handleDateFromChange(e.target.value)}
                className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
              />
            </div>
            <span className="text-white/70 text-xs sm:text-sm">to</span>
            {/* To Date */}
            <div
              className="relative flex-1 sm:flex-none sm:w-40 cursor-pointer"
              onClick={() => dateToRef.current?.showPicker()}
            >
              <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-xs sm:text-sm border border-white/30 rounded-lg bg-white/20 text-white">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/70 flex-shrink-0" />
                <span className={dateTo ? 'text-white' : 'text-white/70'}>
                  {dateTo ? formatDate(dateTo, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                </span>
              </div>
              <input
                ref={dateToRef}
                type="date"
                value={dateTo}
                onChange={(e) => handleDateToChange(e.target.value)}
                min={dateFrom}
                className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={handleClearDates}
                className="text-white/70 hover:text-white px-2 py-1.5 rounded hover:bg-white/10 flex-shrink-0"
                title="Clear dates"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs and Search */}
      <div className="bg-white px-3 sm:px-4 py-2 rounded-t-xl border border-b-0 border-gray-200 flex-shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 lg:gap-4">
          <div className="overflow-x-auto">
            <EmailLogTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              counts={tabCounts}
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-36 sm:w-44 flex-shrink-0">
              <SimpleSelect
                value={emailType}
                onChange={handleEmailTypeChange}
                options={[
                  { label: 'All Types', value: '' },
                  ...(Array.isArray(emailTypeOptions) ? emailTypeOptions : []).map((option) => ({
                    label: option.label,
                    value: option.value,
                  })),
                ]}
                placeholder="Email Type"
                isClearable
              />
            </div>
            <div className="relative flex-1 sm:flex-none sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by email or name..."
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-cyan-500 focus:border-transparent"
              />
              {isFetching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
            </div>
            {/* Include archived toggle — Datadog-style opt-in for cold-storage logs */}
            <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => { setIncludeArchived(e.target.checked); setCurrentPage(1); }}
                className="w-4 h-4 rounded border-gray-300 text-brand-cyan-600 focus:ring-brand-cyan-500"
              />
              <span className="text-sm font-medium text-gray-700">Include archived</span>
            </label>
          </div>
        </div>
      </div>

      {/* Content - Master Detail Layout */}
      <div className="flex-1 flex bg-white rounded-b-xl border border-t-0 border-gray-200 overflow-hidden">
        {/* Left Panel - List */}
        <div className={`${showDetail ? 'hidden lg:flex' : 'flex'} w-full lg:w-[480px] flex-shrink-0 lg:border-r border-gray-200 flex-col overflow-hidden`}>
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <EmailLogList
              logs={logs}
              selectedId={selectedLog?._id}
              onSelect={handleSelectLog}
              isLoading={isLoadingLogs}
            />
          </div>

          {/* Pagination - Fixed at bottom */}
          {totalItems > 0 && (
            <div className="flex-shrink-0 border-t border-gray-200 bg-white">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={(newLimit) => {
                  setItemsPerPage(newLimit);
                  setCurrentPage(1);
                }}
                compact={true}
              />
            </div>
          )}
        </div>

        {/* Right Panel - Email detail */}
        <div className={`${showDetail ? 'flex' : 'hidden lg:flex'} flex-1 flex-col overflow-hidden ${!selectedLog ? 'items-center justify-center' : ''} bg-gray-100`}>
          {/* Mobile back button */}
          {showDetail && selectedLog && (
            <div className="lg:hidden flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-white flex-shrink-0">
              <button
                onClick={handleBackToList}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to list
              </button>
            </div>
          )}
          <div className="h-full w-full overflow-y-auto custom-scrollbar">
            <EmailLogDetail
              log={selectedLog}
              onRetry={handleRetry}
              isRetrying={retryMutation.isPending}
            />
          </div>
        </div>
      </div>

      {/* Error State */}
      {logsError && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg shadow-lg text-sm">
          Failed to load email logs. Please try again.
        </div>
      )}
    </div>
  );
}
